import React, { useState } from "react";
import { View, Text, StyleSheet, Pressable, FlatList } from "react-native";
import {
  COLORS,
  COMMON_STYLES,
  LazyImage,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from "@dehaat/dds";
import TickIcon from "./icons/Tick";
import CrossIcon from "./icons/Cross";
import { SprayingAdvisoryResult, SpraySlot } from "../models/advistory";
import { useTranslation } from "react-i18next";

type Props = {
  data: SprayingAdvisoryResult;
};

type AdvisoryDayType = "today" | "tomorrow";
type AdvisoryDay = {
  value: AdvisoryDayType;
  title: string;
};

const SpraySlotAdvisory: React.FC<Props> = ({ data }) => {
  const { t } = useTranslation();

  const tabs = (
    [
      { value: "today", title: t("today") },
      { value: "tomorrow", title: t("tomorrow") },
    ] as AdvisoryDay[]
  ).filter((tab) => data[tab.value]?.slots?.length > 0);

  const [activeTab, setActiveTab] = useState<AdvisoryDayType>(tabs[0]?.value);

  const slots = data[activeTab]?.slots || [];

  const renderItem = ({ item }: { item: SpraySlot }) => (
    <View style={styles.slotCard}>
      <View style={styles.iconWrapper}>
        {item.is_good ? <TickIcon /> : <CrossIcon />}
      </View>
      <Text style={[TYPOGRAPHY.TEXT2, VARIANTS_STYLES.SEMI_EMPHASIZED]}>
        {item.start} - {item.end}
      </Text>
    </View>
  );

  if (tabs.length === 0) return null;

  return (
    <View style={COMMON_STYLES.mt1}>
      <View style={[COMMON_STYLES.row, COMMON_STYLES.justifyStart]}>
        {tabs.map((tab) => (
          <Pressable
            key={tab.value}
            onPress={() => setActiveTab(tab.value)}
            style={[
              COMMON_STYLES.row,
              COMMON_STYLES.alignItemsCenter,
              styles.tab,
              activeTab === tab.value && styles.activeTab,
            ]}
          >
            <Text
              style={[
                TYPOGRAPHY.TEXT2,
                activeTab === tab.value
                  ? [
                      VARIANTS_STYLES.SEMI_EMPHASIZED,
                      { color: COLORS.primary100 },
                    ]
                  : { color: COLORS.neutral80 },
              ]}
            >
              {tab.title.charAt(0).toUpperCase() + tab.title.slice(1)}
            </Text>
            <LazyImage
              imageUrl={data[tab.value]?.weather_icon}
              imgStyles={{
                width: 14,
                height: 14,
                marginLeft: 6,
                resizeMode: "contain",
              }}
              isResizeMethod
            />
          </Pressable>
        ))}
      </View>

      <FlatList
        data={slots}
        renderItem={renderItem}
        keyExtractor={(item, index) => `${item.start}-${index}`}
        contentContainerStyle={COMMON_STYLES["mt1/2"]}
      />

      <View style={[COMMON_STYLES.ml1, { gap: 10 }]}>
        <View style={[COMMON_STYLES.row, COMMON_STYLES.alignItemsCenter]}>
          <TickIcon width={18} height={18} />
          <Text style={[TYPOGRAPHY.CAPTION2, COMMON_STYLES["ml1/4"]]}>
            {t("slots_for_good_spraying")}
          </Text>
        </View>
        <View style={[COMMON_STYLES.row, COMMON_STYLES.alignItemsCenter]}>
          <CrossIcon width={18} height={18} />
          <Text style={[TYPOGRAPHY.CAPTION2, COMMON_STYLES["ml1/4"]]}>
            {t("slots_for_bad_spraying")}
          </Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  tab: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginRight: 16,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  activeTab: {
    borderBottomColor: COLORS.primary100,
  },
  slotCard: {
    backgroundColor: COLORS.neutral10,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  iconWrapper: {
    width: 30,
    alignItems: "center",
  },
});

export default SpraySlotAdvisory;
